import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tabs,
  Tab,
  Typography,
  Stack,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import type {
  ConfigurationResponseDto,
  CreateConfigurationDto,
  UpdateConfigurationDto,
  Environment,
} from "../../../types/api";

interface ConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateConfigurationDto | UpdateConfigurationDto) => Promise<void>;
  editing?: ConfigurationResponseDto;
  environment: Environment;
}

const ConfigDialog = ({
  open,
  onClose,
  onSave,
  editing,
  environment,
}: ConfigDialogProps) => {
  const [key, setKey] = useState(editing?.configKey || "");
  const [value, setValue] = useState(editing?.configValue || "");
  const [description, setDescription] = useState(editing?.description || "");
  const [isEncrypted, setIsEncrypted] = useState(!!editing?.isEncrypted);
  const [isRequired, setIsRequired] = useState(!!editing?.isRequired);
  const [isSecret, setIsSecret] = useState(!!editing?.isSecret);
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [env, setEnv] = useState<Environment>(editing?.environment || environment);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      setKey(editing.configKey);
      setValue(editing.configValue);
      setDescription(editing.description || "");
      setIsEncrypted(!!editing.isEncrypted);
      setIsRequired(!!editing.isRequired);
      setIsSecret(!!editing.isSecret);
      setIsActive(editing.isActive ?? true);
      setEnv(editing.environment);
    } else {
      setKey("");
      setValue("");
      setDescription("");
      setIsEncrypted(false);
      setIsRequired(false);
      setIsSecret(false);
      setIsActive(true);
      setEnv(environment);
    }
  }, [editing, environment]);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editing) {
        await onSave({
          configValue: value,
          environment: env,
          description,
          isEncrypted,
          isRequired,
          isSecret,
          isActive,
        } as UpdateConfigurationDto);
      } else {
        await onSave({
          configKey: key,
          configValue: value,
          environment: env,
          description,
          isEncrypted,
          isRequired,
          isSecret,
        } as CreateConfigurationDto);
      }
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing ? "Edit Configuration" : "Add Configuration"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {!editing && (
            <TextField
              label="Key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              fullWidth
              required
            />
          )}
          <TextField
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            required
            multiline
            rows={3}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Encrypted</InputLabel>
            <Select
              value={isEncrypted ? "yes" : "no"}
              onChange={(e) => setIsEncrypted(e.target.value === "yes")}
              label="Encrypted"
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Required</InputLabel>
            <Select
              value={isRequired ? "yes" : "no"}
              onChange={(e) => setIsRequired(e.target.value === "yes")}
              label="Required"
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Secret</InputLabel>
            <Select
              value={isSecret ? "yes" : "no"}
              onChange={(e) => setIsSecret(e.target.value === "yes")}
              label="Secret"
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
          {editing && (
            <FormControl fullWidth>
              <InputLabel>Active</InputLabel>
              <Select
                value={isActive ? "yes" : "no"}
                onChange={(e) => setIsActive(e.target.value === "yes")}
                label="Active"
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth>
            <InputLabel>Environment</InputLabel>
            <Select
              value={env}
              onChange={(e) => setEnv(e.target.value as Environment)}
              label="Environment"
            >
              <MenuItem value="production">Production</MenuItem>
              <MenuItem value="development">Development</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ProviderConfigurations = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [configurations, setConfigurations] = useState<
    ConfigurationResponseDto[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ConfigurationResponseDto | undefined>();
  const [selectedEnv, setSelectedEnv] = useState<Environment>("production");

  useEffect(() => {
    if (record?.id) {
      loadConfigurations();
    }
  }, [record?.id, selectedEnv]);

  const loadConfigurations = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const response = await fetch(
        apiUrl(
          `/providers/${record.id}/config${
            selectedEnv ? `?environment=${selectedEnv}` : ""
          }`,
        ),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        // prefer response.data (API v2), fall back to response.configurations (older API), or the response itself
        const rawConfigs = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.configurations)
          ? data.configurations
          : Array.isArray(data)
          ? data
          : [];

        // Normalize environment to a lowercase string for consistent filtering.
        const configs = rawConfigs.map((c: any) => {
          // prefer explicit fields 'environment' then 'env'
          let envValue: any = c.environment ?? c.env;

          if (envValue && typeof envValue === "object") {
            envValue = envValue.name ?? envValue.value ?? String(envValue);
          }

          const normalizedEnv = typeof envValue === "string" ? envValue.toLowerCase() : undefined;

          return {
            ...c,
            // ensure environment is a readable string for display
            environment: typeof envValue === "string" ? envValue : String(envValue ?? ""),
            normalizedEnvironment: normalizedEnv,
          } as ConfigurationResponseDto & { normalizedEnvironment?: string };
        });

        setConfigurations(configs);
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (config: ConfigurationResponseDto) => {
    setEditing(config);
    setDialogOpen(true);
  };

  const handleDelete = async (key: string, environment: Environment) => {
    if (!record?.id) return;
    if (!confirm(`Delete configuration "${key}"?`)) return;

    try {
      const response = await fetch(
        apiUrl(
          `/providers/${record.id}/config/${key}?environment=${environment}`,
        ),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        notify("Configuration deleted successfully", { type: "success" });
        loadConfigurations();
      } else {
        throw new Error("Failed to delete configuration");
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    }
  };

  const handleSave = async (
    data: CreateConfigurationDto | UpdateConfigurationDto,
  ) => {
    if (!record?.id) return;

    try {
      if (editing) {
        const response = await fetch(
          apiUrl(
            `/providers/${record.id}/config/${editing.key}`,
          ),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            },
            body: JSON.stringify(data),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to update configuration");
        }
        notify("Configuration updated successfully", { type: "success" });
      } else {
        const response = await fetch(
          apiUrl(`/providers/${record.id}/config`),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            },
            body: JSON.stringify(data),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to create configuration");
        }
        notify("Configuration added successfully", { type: "success" });
      }
      loadConfigurations();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
      throw error;
    }
  };

  if (!record) return null;

  const filteredConfigs = configurations.filter((config) => {
    const cfgEnv = (config as any).normalizedEnvironment || (typeof config.environment === 'string' ? config.environment.toLowerCase() : String(config.environment).toLowerCase());
    return cfgEnv === String(selectedEnv).toLowerCase();
  });

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Tabs
          value={selectedEnv}
          onChange={(_e, value) => setSelectedEnv(value as Environment)}
        >
          <Tab label="Production" value="production" />
          <Tab label="Development" value="development" />
        </Tabs>
        <Button
          startIcon={<Add />}
          onClick={handleAdd}
          variant="contained"
          size="small"
        >
          Add Configuration
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading configurations...</Typography>
      ) : filteredConfigs.length === 0 ? (
        <Typography color="text.secondary">
          No configurations found for {selectedEnv} environment
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConfigs.map((config) => (
                <TableRow key={`${config.configKey}-${config.environment}`}>
                  <TableCell>{config.configKey}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {config.configValue}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {config.description || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {config.isEncrypted && <Chip label="Encrypted" size="small" />}
                      {config.isRequired && <Chip label="Required" size="small" />}
                      {config.isSecret && <Chip label="Secret" size="small" />}
                      {!config.isActive && <Chip label="Inactive" size="small" color="warning" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={config.environment} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(config)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(config.configKey, config.environment)}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfigDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(undefined);
        }}
        onSave={handleSave}
        editing={editing}
        environment={selectedEnv}
      />
    </Box>
  );
};
